import { createContext, FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { Territory } from 'softcat:config/territory.ts';
import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';

export type ContextDefaults = {
    contextDefaults?: {
        activeTerritory?: Territory;
        territories?: Territory[];
    };
};

export type TerritoryContextType = {
    activeTerritory: Territory | undefined;
    setTerritories: (territories: Territory[]) => void;
    switchTerritory: (territory: Territory) => void;
    territories: Territory[];
};

const TerritoryContext = createContext<TerritoryContextType | undefined>(undefined);

const TerritoryProvider: FunctionComponent<PropsWithChildren<ContextDefaults>> = ({ children, contextDefaults = {} }) => {
    const defaultTerritory = (localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey) as Territory) || undefined;
    const [activeTerritory, setActiveTerritory] = useState<Territory>(contextDefaults.activeTerritory || defaultTerritory);
    const [territories, setTerritories] = useState<Territory[]>(contextDefaults.territories || []);

    const switchTerritory = useCallback((territory: Territory) => {
        localStorage.setItem(UserPreferencesConfig.TerritoryStorageKey, territory);

        setActiveTerritory(territory);
    }, []);

    useEffect(() => {
        if (territories.length > 0 && (!activeTerritory || !territories.includes(activeTerritory))) {
            switchTerritory(territories[0]);
        }
    }, [activeTerritory, switchTerritory, territories]);

    const territoryContextMemo = useMemo<TerritoryContextType>(() => {
        return {
            activeTerritory,
            setTerritories,
            switchTerritory,
            territories,
        };
    }, [activeTerritory, switchTerritory, territories]);

    return <TerritoryContext.Provider value={territoryContextMemo}>{children}</TerritoryContext.Provider>;
};

export { TerritoryContext, TerritoryProvider };
